import axios from 'axios';

// Formats axios errors into user-friendly messages
const handleFetchError = (err, defaultMessage, setErrorFn) => {
  let errorMessage = defaultMessage;

  if (err.response) {
    if (err.response.status === 401 || err.response.status === 403) {
      errorMessage = 'Authentication error. Please log in again.';
    } else {
      errorMessage += `: ${err.response.data?.message || err.response.statusText || err.message}`;
    }
  } else if (err.request) {
    errorMessage += ': No response from server. Please check your connection.';
  } else {
    errorMessage += `: ${err.message}`;
  }

  setErrorFn(errorMessage);
};

// Authenticated axios request supporting GET, POST, DELETE via method option
const makeAuthenticatedRequest = async (url, token, { method = 'get', data } = {}) => {
  if (!token) {
    throw new Error('No authentication token available');
  }
  return await axios({
    method,
    url,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    ...(data !== undefined && { data })
  });
};

// /auth/user_history caps `limit` at 200 server-side and defaults to 50, so a
// single request only ever returns the first page. The Manage grid paginates
// client-side over whatever it was handed, which meant history beyond that
// first page was unreachable. Walk the pagination envelope instead.
const HISTORY_PAGE_LIMIT = 200;
const MAX_HISTORY_PAGES = 25; // 5000 rows — a backstop, not an expected limit

const withPageParams = (baseUrl, page) =>
  `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}page=${page}&limit=${HISTORY_PAGE_LIMIT}`;

// Returns the page-1 axios response with `data.data` replaced by every row
// across all pages, deduped by _id (pages are skip/limit over a created_at
// sort, so a row written mid-fetch can otherwise appear twice).
const fetchAllHistoryPages = async (baseUrl, token) => {
  const first = await makeAuthenticatedRequest(withPageParams(baseUrl, 1), token);
  const firstRows = first.data?.data;
  if (!Array.isArray(firstRows)) return first; // let the caller report bad shape

  const reportedPages = first.data?.pagination?.total_pages || 1;
  const totalPages = Math.min(reportedPages, MAX_HISTORY_PAGES);
  if (reportedPages > MAX_HISTORY_PAGES) {
    console.warn(
      `[user_history] ${reportedPages} pages available, fetching first ${MAX_HISTORY_PAGES}`
    );
  }
  if (totalPages <= 1) return first;

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      makeAuthenticatedRequest(withPageParams(baseUrl, i + 2), token))
  );

  const seen = new Set();
  const allRows = [];
  [firstRows, ...rest.map(r => r.data?.data)].forEach(rows => {
    if (!Array.isArray(rows)) return;
    rows.forEach(row => {
      const id = row?._id;
      if (id && seen.has(id)) return;
      if (id) seen.add(id);
      allRows.push(row);
    });
  });

  return { ...first, data: { ...first.data, data: allRows } };
};

export { handleFetchError, makeAuthenticatedRequest, fetchAllHistoryPages };
